import { createGlobalStyle } from 'styled-components';

export const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;
  }

  body {
    font-family: ${({ theme }) => theme.fonts.primary};
    font-size: ${({ theme }) => theme.fontSizes.base};
    font-weight: ${({ theme }) => theme.fontWeights.normal};
    line-height: 1.6;
    color: ${({ theme }) => theme.colors.textPrimary};
    background-color: ${({ theme }) => theme.colors.background};
    transition: ${({ theme }) => theme.transitions.default};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  #root {
    min-height: 100vh;
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    line-height: 1.3;
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }

  h1 { font-size: ${({ theme }) => theme.fontSizes['3xl']}; }
  h2 { font-size: ${({ theme }) => theme.fontSizes['2xl']}; }
  h3 { font-size: ${({ theme }) => theme.fontSizes.xl}; }
  h4 { font-size: ${({ theme }) => theme.fontSizes.lg}; }
  h5 { font-size: ${({ theme }) => theme.fontSizes.base}; }
  h6 { font-size: ${({ theme }) => theme.fontSizes.sm}; }

  p {
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }

  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    transition: ${({ theme }) => theme.transitions.default};

    &:hover {
      color: ${({ theme }) => theme.colors.primaryHover};
      text-decoration: underline;
    }
  }

  button {
    font-family: inherit;
    cursor: pointer;
    border: none;
    outline: none;
    transition: ${({ theme }) => theme.transitions.default};

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  input, textarea, select {
    font-family: inherit;
    font-size: inherit;
    outline: none;
    transition: ${({ theme }) => theme.transitions.default};
  }

  /* Scrollbar styles */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.backgroundSecondary};
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.full};

    &:hover {
      background: ${({ theme }) => theme.colors.borderHover};
    }
  }

  /* Loading spinner animation */
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideInFromRight {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out;
  }

  .animate-slideInFromRight {
    animation: slideInFromRight 0.3s ease-out;
  }

  /* Responsive Typography */
  @media (max-width: 768px) {
    html {
      font-size: 14px;
    }
    
    h1 { font-size: ${({ theme }) => theme.fontSizes['2xl']}; }
    h2 { font-size: ${({ theme }) => theme.fontSizes.xl}; }
    h3 { font-size: ${({ theme }) => theme.fontSizes.lg}; }
  }

  @media (max-width: 480px) {
    html {
      font-size: 12px;
    }
  }

  /* Touch and Mobile Optimizations */
  @media (hover: none) and (pointer: coarse) {
    /* Mobile touch devices */
    button, a, [role="button"] {
      min-height: 44px; /* iOS recommended touch target size */
      min-width: 44px;
    }
    
    /* Disable hover effects on touch devices */
    button:hover, a:hover {
      transform: none;
    }
  }

  /* iOS Safari specific fixes */
  @supports (-webkit-touch-callout: none) {
    /* iOS Safari */
    body {
      -webkit-text-size-adjust: 100%;
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    }
    
    input[type="text"], input[type="email"], input[type="password"], textarea {
      -webkit-appearance: none;
      border-radius: 0;
    }
  }

  /* Android Chrome specific fixes */
  @media screen and (-webkit-device-pixel-ratio: 1) {
    /* Android Chrome */
    body {
      -webkit-text-size-adjust: none;
    }
  }

  /* High DPI screens */
  @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
    body {
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
  }

  /* Landscape mobile orientation */
  @media screen and (orientation: landscape) and (max-height: 500px) {
    /* Reduce spacing on landscape mobile */
    h1, h2, h3, h4, h5, h6 {
      margin-bottom: ${({ theme }) => theme.spacing.xs};
    }
    
    p {
      margin-bottom: ${({ theme }) => theme.spacing.sm};
    }
  }

  /* Dark mode media query for system preference */
  @media (prefers-color-scheme: dark) {
    /* System dark mode styles are handled by ThemeProvider */
  }

  /* Reduced motion preference */
  @media (prefers-reduced-motion: reduce) {
    *, ::before, ::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
    
    html {
      scroll-behavior: auto;
    }
  }

  /* Focus styles for accessibility */
  :focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  /* High contrast mode support */
  @media (prefers-contrast: high) {
    button, input, select, textarea {
      border: 2px solid;
    }
  }

  /* Print styles */
  @media print {
    * {
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    
    body {
      background: white !important;
      color: black !important;
    }
    
    /* Hide non-essential elements when printing */
    nav, .sidebar, .mobile-nav, button:not(.print-button) {
      display: none !important;
    }
  }

  /* RTL Support */
  body.rtl {
    direction: rtl;
    text-align: right;
  }

  body.rtl * {
    font-family: ${({ theme }) => theme.fonts.primary}, 'Arial', sans-serif;
  }

  /* RTL specific text inputs */
  body.rtl input,
  body.rtl textarea,
  body.rtl select {
    text-align: right;
    direction: rtl;
  }

  /* RTL navigation adjustments */
  body.rtl .nav-item {
    text-align: right;
  }

  /* RTL card adjustments */
  body.rtl .card-number {
    direction: ltr;
    text-align: center;
  }

  /* RTL currency formatting */
  body.rtl .currency {
    direction: ltr;
    display: inline-block;
  }

  /* Language-specific font optimization */
  body[lang="he"], body.rtl, body.lang-he {
    font-family: "Noto Sans Hebrew", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
  }

  body[lang="ja"], body.lang-ja {
    font-family: "Noto Sans JP", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  body[lang="ar"], body.lang-ar {
    font-family: "Noto Sans Arabic", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
    direction: rtl;
  }

  body[lang="de"], body.lang-de {
    font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  body[lang="fr"], body.lang-fr {
    font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  body[lang="es"], body.lang-es {
    font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  /* Improve rendering for CJK characters */
  body[lang="ja"] *,
  body[lang="ko"] *,
  body[lang="zh"] *,
  body.lang-ja *,
  body.lang-ko *,
  body.lang-zh * {
    font-feature-settings: "liga" 0;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* Improve Hebrew text rendering */
  body[lang="he"] *,
  body.rtl *,
  body.lang-he * {
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;
